import { useCallback, useEffect, useRef, useState } from 'react'

const SERIAL_BAUD_RATE = 115200
const SERIAL_DELIMITER = '\r'

function wait(milliseconds) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds)
  })
}

export default function useSerialConnection({ onData }) {
  const [portState, setPortState] = useState('closed')
  const [isBusy, setIsBusy] = useState(false)
  const portRef = useRef(null)
  const readerRef = useRef(null)
  const writerRef = useRef(null)
  const readLoopRef = useRef(null)
  const onDataRef = useRef(onData)
  const isBusyRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    onDataRef.current = onData
  }, [onData])

  const updateBusy = useCallback((nextBusy) => {
    isBusyRef.current = nextBusy

    if (isMountedRef.current) {
      setIsBusy(nextBusy)
    }
  }, [])

  const closePort = useCallback(async ({ clearPort = true, updateState = true } = {}) => {
    const reader = readerRef.current
    const writer = writerRef.current
    const port = portRef.current

    readerRef.current = null
    writerRef.current = null

    if (reader) {
      await reader.cancel().catch(() => {})
    }

    if (readLoopRef.current) {
      await readLoopRef.current.catch(() => {})
      readLoopRef.current = null
    }

    if (writer) {
      try {
        writer.releaseLock()
      } catch {
        // The stream may already have released its lock after a device reset.
      }
    }

    if (port) {
      await port.close().catch(() => {})
    }

    if (clearPort) {
      portRef.current = null
    }

    if (updateState && isMountedRef.current) {
      setPortState('closed')
    }
  }, [])

  const openPort = useCallback(async (port) => {
    await port.open({ baudRate: SERIAL_BAUD_RATE })
    portRef.current = port

    const reader = port.readable.getReader()
    const writer = port.writable.getWriter()
    const decoder = new TextDecoder()
    const encoder = new TextEncoder()

    readerRef.current = reader
    writerRef.current = writer

    readLoopRef.current = (async () => {
      let buffer = ''

      try {
        while (true) {
          const { value, done } = await reader.read()

          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })
          const messages = buffer.split(/\r\n|\r|\n/)
          buffer = messages.pop() || ''

          messages.forEach((message) => {
            const trimmedMessage = message.trim()

            if (!trimmedMessage) {
              return
            }

            console.log('[Serial RX]', trimmedMessage)

            try {
              onDataRef.current?.(JSON.parse(trimmedMessage))
            } catch {
              // Boards can emit boot logs alongside the JSON configuration.
            }
          })
        }
      } catch {
        if (readerRef.current === reader && isMountedRef.current) {
          setPortState('closed')
        }
      } finally {
        try {
          reader.releaseLock()
        } catch {
          // The disconnect path may have already released the reader.
        }
      }
    })()

    if (isMountedRef.current) {
      setPortState('open')
    }

    await writer.write(encoder.encode(`{"cmd":"get"}${SERIAL_DELIMITER}`))
  }, [])

  const connect = useCallback(async () => {
    if (isBusyRef.current) {
      return
    }

    if (!('serial' in navigator)) {
      throw new Error('Web Serial is not supported. Use Chrome or Edge on desktop over HTTPS.')
    }

    updateBusy(true)

    try {
      const port = await navigator.serial.requestPort()
      await openPort(port)
    } catch (error) {
      await closePort()
      throw error
    } finally {
      updateBusy(false)
    }
  }, [closePort, openPort, updateBusy])

  const disconnect = useCallback(async () => {
    if (isBusyRef.current) {
      return
    }

    updateBusy(true)

    try {
      await closePort()
    } finally {
      updateBusy(false)
    }
  }, [closePort, updateBusy])

  const sendAndReconnect = useCallback(async (configuration) => {
    if (isBusyRef.current) {
      throw new Error('The serial connection is busy.')
    }

    const port = portRef.current
    const writer = writerRef.current

    if (!port || !writer) {
      throw new Error('Connect a serial device before saving.')
    }

    updateBusy(true)

    try {
      const encoder = new TextEncoder()
      await writer.write(encoder.encode(`${JSON.stringify(configuration)}${SERIAL_DELIMITER}`))
      await wait(1000)
      await closePort({ clearPort: false, updateState: false })
      await openPort(port)
    } catch (error) {
      await closePort()
      throw error
    } finally {
      updateBusy(false)
    }
  }, [closePort, openPort, updateBusy])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      closePort({ updateState: false })
    }
  }, [closePort])

  return {
    connect,
    disconnect,
    sendAndReconnect,
    portState,
    isBusy,
    isSupported: typeof navigator !== 'undefined' && 'serial' in navigator,
  }
}
